// ═══════════════════════════════════════════════════════════════════════
// Éléments communs aux écrans IPM (page IPM, Équipe IPM).
//   • useApi : appels /api/ipm avec le jeton de session ;
//   • Fenetre : fenêtre modale (Échap ferme seulement la fenêtre du dessus) ;
//   • FenetreSaisie : petit formulaire (motif, date…) ; le message d'erreur du serveur est
//     affiché tel quel.
// Socle visuel SOGAS RH : icônes lucide uniquement, aucun emoji.
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { X, Check, Copy } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Bouton, BandeauInfo, NON_RENSEIGNE } from '../../design/composants';

export const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
export const fmtF = (n) => (n === null || n === undefined ? NON_RENSEIGNE : `${Number(n).toLocaleString('fr-FR')} FCFA`);
export const messageErreur = (e, defaut = 'L\'opération n\'a pas abouti.') => e?.response?.data?.message || defaut;
export const nomSalarie = (x) => `${x.prenom || ''} ${(x.nom || '').toUpperCase()}`.trim();
export const aujourdhuiIso = () => new Date().toISOString().slice(0, 10);

/** Ajoute n mois à une date AAAA-MM-JJ (même règle que le serveur : jour borné à la fin du mois). */
export function ajouterMois(iso, n) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return null;
  const [a, m, j] = iso.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1 + n, 1));
  const dernier = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(j, dernier));
  return d.toISOString().slice(0, 10);
}

export function useApi() {
  const { token } = useAuth();
  return useMemo(() => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    return {
      get: (u, params) => axios.get(`${API}/ipm${u}`, { ...h, params }).then((r) => r.data),
      post: (u, body) => axios.post(`${API}/ipm${u}`, body || {}, h).then((r) => r.data),
      patch: (u, body) => axios.patch(`${API}/ipm${u}`, body || {}, h).then((r) => r.data),
    };
  }, [token]);
}

// Pile des fenêtres ouvertes : Échap ne ferme que celle du dessus.
const pileFenetres = [];

/** Fenêtre modale. */
export function Fenetre({ titre, onFermer, children, large = false }) {
  const jeton = useRef({});
  const fermer = useRef(onFermer);
  useEffect(() => { fermer.current = onFermer; });
  useEffect(() => {
    const moi = jeton.current;
    pileFenetres.push(moi);
    const echap = (e) => {
      if (e.key === 'Escape' && pileFenetres[pileFenetres.length - 1] === moi) fermer.current();
    };
    document.addEventListener('keydown', echap);
    return () => {
      document.removeEventListener('keydown', echap);
      const i = pileFenetres.indexOf(moi);
      if (i >= 0) pileFenetres.splice(i, 1);
    };
  }, []);
  return (
    <div role="dialog" aria-modal="true" aria-label={titre}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div className="rh-carte" style={{ width: large ? 880 : 520, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: 'var(--sg-ombre-haute)', marginBottom: 0 }}>
        <div className="rh-carte-entete">
          <h2 className="rh-carte-titre" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{titre}</h2>
          <Bouton variante="discret" className="rh-btn-icone" Icone={X} onClick={onFermer} aria-label="Fermer" />
        </div>
        {children}
      </div>
    </div>
  );
}

export function ChampForm({ libelle, requis, aide, children, id }) {
  return (
    <div style={{ marginBottom: 12, minWidth: 0 }}>
      <label htmlFor={id} style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
        {libelle}{requis && <span className="rh-form-requis" aria-hidden="true">*</span>}
      </label>
      {children}
      {aide && <div className="rh-form-aide">{aide}</div>}
    </div>
  );
}

export const PiedFenetre = ({ children }) => <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>{children}</div>;

/** Case à cocher avec son libellé (et une aide facultative). */
export function CaseACocher({ coche, onChange, libelle, aide, disabled }) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, marginBottom: 12, opacity: disabled ? 0.6 : 1 }}>
      <input type="checkbox" checked={!!coche} disabled={disabled} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 3 }} />
      <span>{libelle}{aide && <span className="rh-form-aide" style={{ display: 'block' }}>{aide}</span>}</span>
    </label>
  );
}

/**
 * Petit formulaire en fenêtre. `champs` : [{ cle, libelle, requis, aide, type, min, max, options, texteLong }]
 * (type 'checkbox' : case à cocher). `enfant(v, setV)` ajoute un contenu libre sous les champs.
 */
export function FenetreSaisie({ titre, champs, initial = {}, valider, onFermer, onFait, avertissement, tonAvertissement = 'info', enfant, libelleBouton = 'Enregistrer', desactive }) {
  const [v, setV] = useState(initial);
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const soumettre = async () => {
    setEnvoi(true); setErreur(null);
    try { const r = await valider(v); onFait(r); } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Fenetre titre={titre} onFermer={onFermer}>
      {avertissement && <BandeauInfo ton={tonAvertissement}>{avertissement}</BandeauInfo>}
      {champs.map((c) => (c.type === 'checkbox' ? (
        <CaseACocher key={c.cle} coche={v[c.cle]} libelle={c.libelle} aide={c.aide} onChange={(x) => setV({ ...v, [c.cle]: x })} />
      ) : (
        <ChampForm key={c.cle} libelle={c.libelle} requis={c.requis} aide={c.aide} id={`f-${c.cle}`}>
          {c.options ? (
            <select id={`f-${c.cle}`} className="rh-champ-saisie" value={v[c.cle] ?? ''} onChange={(e) => setV({ ...v, [c.cle]: e.target.value })}>
              {c.options.map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
            </select>
          ) : c.texteLong ? (
            <textarea id={`f-${c.cle}`} className="rh-champ-saisie" rows={3} value={v[c.cle] ?? ''} onChange={(e) => setV({ ...v, [c.cle]: e.target.value })} />
          ) : (
            <input id={`f-${c.cle}`} className="rh-champ-saisie" type={c.type || 'text'} min={c.min} max={c.max} value={v[c.cle] ?? ''} onChange={(e) => setV({ ...v, [c.cle]: e.target.value })} />
          )}
        </ChampForm>
      )))}
      {enfant && enfant(v, setV)}
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <PiedFenetre>
        <Bouton variante="discret" onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={Check} onClick={soumettre} disabled={envoi || (desactive ? desactive(v) : false)}>{envoi ? 'Enregistrement…' : libelleBouton}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

/** Copie un texte dans le presse-papiers (repli sans API Clipboard, ex. page non sécurisée). */
export async function copierTexte(texte) {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(texte); return true; }
  } catch (e) { /* repli ci-dessous */ }
  try {
    const zone = document.createElement('textarea');
    zone.value = texte; zone.setAttribute('readonly', ''); zone.style.position = 'fixed'; zone.style.opacity = '0';
    document.body.appendChild(zone); zone.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(zone);
    return ok;
  } catch (e) { return false; }
}

/** Valeur affichée une seule fois (identifiant, mot de passe provisoire) avec bouton « Copier ». */
export function ValeurACopier({ libelle, valeur, police = 'monospace' }) {
  const [copie, setCopie] = useState(false);
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="rh-champ-libelle">{libelle}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <code style={{ fontFamily: police, fontSize: 16, fontWeight: 600, padding: '6px 10px', background: 'var(--sg-fond, #f4f5f7)', border: '1px solid var(--sg-bord)', borderRadius: 6, overflowWrap: 'anywhere', userSelect: 'all' }}>{valeur}</code>
        <Bouton petit Icone={copie ? Check : Copy} onClick={async () => { setCopie(await copierTexte(valeur)); }}>{copie ? 'Copié' : 'Copier'}</Bouton>
      </div>
    </div>
  );
}
